import "@/fonts";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { MsalProvider } from "@azure/msal-react";
import { EventType, type EventMessage, type AuthenticationResult } from "@azure/msal-browser";
import { msalInstance, isMsalConfigured, clearStuckInteractionState } from "@/authConfig";
import App from "./App";
import ConfigMissingScreen from "./pages/Login/ConfigMissingScreen";

async function bootstrap() {
  const root = createRoot(document.getElementById("root")!);

  if (!isMsalConfigured) {
    // Deliberately never call any MSAL API in this branch - see
    // authConfig.ts for why doing so with missing/placeholder config is
    // what causes the "interaction_in_progress" lock in the first place.
    root.render(
      <StrictMode>
        <ConfigMissingScreen />
      </StrictMode>
    );
    return;
  }

  // A stale flag can survive an interrupted redirect (page reload mid-flow,
  // dev-server HMR reloading this module during the AAD round trip, or a
  // previous run before .env.local existed). Only clear it when this boot
  // ISN'T processing a genuine redirect response - clearing it unconditionally
  // also wipes the interaction state handleRedirectPromise() needs below to
  // recognize AAD's response, which makes it silently resolve null instead of
  // completing sign-in (confirmed via a live redirect capture: zero token
  // requests fired, handleRedirectPromise() resolved null immediately).
  const isProcessingRedirectResponse = /[#?](?:[^#]*&)?(code|error)=/.test(window.location.hash);
  if (!isProcessingRedirectResponse) {
    clearStuckInteractionState();
  }

  await msalInstance.initialize();

  // Handles the redirect response after AAD sends the user back post-login.
  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response?.account) {
      msalInstance.setActiveAccount(response.account);
    } else if (msalInstance.getAllAccounts().length > 0) {
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }
  } catch (err) {
    console.error("Sign-in redirect handling failed:", err);
  }

  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const result = event.payload as AuthenticationResult;
      msalInstance.setActiveAccount(result.account);
    }
  });

  root.render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </StrictMode>
  );
}

bootstrap();

